// Trigonometry Calculators helpers: sine, cosine, tangent, cotangent/
// secant/cosecant, the three inverse functions, a right-triangle solver,
// the laws of sines and cosines, unit circle coordinates, and a standard-
// angle value table. Angle unit conversion (degrees/radians/gradians/etc.)
// already lives in lib/tools/angleConverter.ts - this file is specifically
// about the trig functions themselves.
import { t } from './i18n'

function toRadians(deg: number): number {
    return (deg * Math.PI) / 180
}
function toDegrees(rad: number): number {
    return (rad * 180) / Math.PI
}
function angleToDegrees(value: unknown, unit: unknown): number {
    const raw = Number(value) || 0
    return String(unit) === 'radian' ? toDegrees(raw) : raw
}
function isNearZero(value: number): boolean {
    return Math.abs(value) < 1e-9
}

const UNDEFINED_LABEL: Record<string, string> = {
    en: 'Undefined', ru: 'Не определено', lv: 'Nav definēts', pl: 'Nieokreślone',
    es: 'No definido', fr: 'Non défini', it: 'Non definito', de: 'Undefiniert',
}
const NO_SOLUTION_LABEL: Record<string, string> = {
    en: 'No valid triangle', ru: 'Треугольник невозможен', lv: 'Trīsstūris nav iespējams', pl: 'Trójkąt niemożliwy',
    es: 'Triángulo no válido', fr: 'Triangle impossible', it: 'Triangolo non valido', de: 'Kein gültiges Dreieck',
}

// ============================================================
// 1. Sine Calculator
// ============================================================
export function sineCalculator(params: { angle_value: unknown; angle_unit: unknown }): { result: number } {
    const deg = angleToDegrees(params.angle_value, params.angle_unit)
    return { result: Math.sin(toRadians(deg)) }
}

// ============================================================
// 2. Cosine Calculator
// ============================================================
export function cosineCalculator(params: { angle_value: unknown; angle_unit: unknown }): { result: number } {
    const deg = angleToDegrees(params.angle_value, params.angle_unit)
    return { result: Math.cos(toRadians(deg)) }
}

// ============================================================
// 3. Tangent Calculator
// ============================================================
export function tangentCalculator(params: { angle_value: unknown; angle_unit: unknown; language: unknown }): { result: number | string } {
    const deg = angleToDegrees(params.angle_value, params.angle_unit)
    if (isNearZero(Math.cos(toRadians(deg)))) return { result: t(UNDEFINED_LABEL, params.language) }
    return { result: Math.tan(toRadians(deg)) }
}

// ============================================================
// 4. Cotangent, Secant & Cosecant Calculator
// ============================================================
export function cotSecCscCalculator(params: { angle_value: unknown; angle_unit: unknown; language: unknown }): {
    cotangent: number | string; secant: number | string; cosecant: number | string
} {
    const deg = angleToDegrees(params.angle_value, params.angle_unit)
    const rad = toRadians(deg)
    const sinV = Math.sin(rad)
    const cosV = Math.cos(rad)
    const undefinedText = t(UNDEFINED_LABEL, params.language)
    return {
        cotangent: isNearZero(sinV) ? undefinedText : cosV / sinV,
        secant: isNearZero(cosV) ? undefinedText : 1 / cosV,
        cosecant: isNearZero(sinV) ? undefinedText : 1 / sinV,
    }
}

// ============================================================
// 5. Inverse Sine (Arcsin) Calculator
// ============================================================
export function arcsinCalculator(params: { value: unknown; language: unknown }): { angle_degrees: number | string; angle_radians: number | string } {
    const value = Number(params.value)
    if (isNaN(value) || value < -1 || value > 1) {
        const undefinedText = t(UNDEFINED_LABEL, params.language)
        return { angle_degrees: undefinedText, angle_radians: undefinedText }
    }
    const rad = Math.asin(value)
    return { angle_degrees: toDegrees(rad), angle_radians: rad }
}

// ============================================================
// 6. Inverse Cosine (Arccos) Calculator
// ============================================================
export function arccosCalculator(params: { value: unknown; language: unknown }): { angle_degrees: number | string; angle_radians: number | string } {
    const value = Number(params.value)
    if (isNaN(value) || value < -1 || value > 1) {
        const undefinedText = t(UNDEFINED_LABEL, params.language)
        return { angle_degrees: undefinedText, angle_radians: undefinedText }
    }
    const rad = Math.acos(value)
    return { angle_degrees: toDegrees(rad), angle_radians: rad }
}

// ============================================================
// 7. Inverse Tangent (Arctan) Calculator
// ============================================================
export function arctanCalculator(params: { value: unknown }): { angle_degrees: number; angle_radians: number } {
    const value = Number(params.value) || 0
    const rad = Math.atan(value)
    return { angle_degrees: toDegrees(rad), angle_radians: rad }
}

// ============================================================
// 8. Right Triangle Trigonometry Solver
// ============================================================
export function rightTriangleSolver(params: { angle_degrees: unknown; known_side_type: unknown; known_side_value: unknown }): {
    opposite: number; adjacent: number; hypotenuse: number; other_angle_degrees: number
} {
    const angle = Math.min(Math.max(Number(params.angle_degrees) || 0, 0.0001), 89.9999)
    const knownType = String(params.known_side_type || 'hypotenuse')
    const knownValue = Math.max(Number(params.known_side_value) || 1, 0)
    const rad = toRadians(angle)

    let hypotenuse: number, opposite: number, adjacent: number
    if (knownType === 'opposite') {
        opposite = knownValue
        hypotenuse = opposite / Math.sin(rad)
        adjacent = hypotenuse * Math.cos(rad)
    } else if (knownType === 'adjacent') {
        adjacent = knownValue
        hypotenuse = adjacent / Math.cos(rad)
        opposite = hypotenuse * Math.sin(rad)
    } else {
        hypotenuse = knownValue
        opposite = hypotenuse * Math.sin(rad)
        adjacent = hypotenuse * Math.cos(rad)
    }

    return { opposite, adjacent, hypotenuse, other_angle_degrees: 90 - angle }
}

// ============================================================
// 9. Law of Sines Calculator (ASA/AAS: two angles + one side)
// ============================================================
export function lawOfSinesCalculator(params: { angle_a_degrees: unknown; side_a: unknown; angle_b_degrees: unknown; language: unknown }): {
    angle_c_degrees: number | string; side_b: number | string; side_c: number | string
} {
    const angleA = Number(params.angle_a_degrees) || 0
    const angleB = Number(params.angle_b_degrees) || 0
    const sideA = Math.max(Number(params.side_a) || 0, 0)
    const angleC = 180 - angleA - angleB

    if (angleA <= 0 || angleB <= 0 || angleC <= 0) {
        const errText = t(NO_SOLUTION_LABEL, params.language)
        return { angle_c_degrees: errText, side_b: errText, side_c: errText }
    }

    const sinA = Math.sin(toRadians(angleA))
    const sideB = (sideA * Math.sin(toRadians(angleB))) / sinA
    const sideC = (sideA * Math.sin(toRadians(angleC))) / sinA
    return { angle_c_degrees: angleC, side_b: sideB, side_c: sideC }
}

// ============================================================
// 10. Law of Cosines Calculator (SAS: two sides + included angle)
// ============================================================
export function lawOfCosinesCalculator(params: { side_a: unknown; side_b: unknown; angle_c_degrees: unknown; language: unknown }): {
    side_c: number | string; angle_a_degrees: number | string; angle_b_degrees: number | string
} {
    const a = Math.max(Number(params.side_a) || 0, 0.0001)
    const b = Math.max(Number(params.side_b) || 0, 0.0001)
    const angleC = Number(params.angle_c_degrees) || 0

    if (angleC <= 0 || angleC >= 180) {
        const errText = t(NO_SOLUTION_LABEL, params.language)
        return { side_c: errText, angle_a_degrees: errText, angle_b_degrees: errText }
    }

    const c = Math.sqrt(a * a + b * b - 2 * a * b * Math.cos(toRadians(angleC)))
    const cosA = (b * b + c * c - a * a) / (2 * b * c)
    const angleA = toDegrees(Math.acos(Math.min(Math.max(cosA, -1), 1)))
    const angleB = 180 - angleC - angleA
    return { side_c: c, angle_a_degrees: angleA, angle_b_degrees: angleB }
}

// ============================================================
// 11. Unit Circle Calculator
// ============================================================
export function unitCircleCalculator(params: { angle_value: unknown; angle_unit: unknown; language: unknown }): {
    x_coordinate: number; y_coordinate: number; reference_angle_degrees: number; quadrant: string
} {
    const deg = angleToDegrees(params.angle_value, params.angle_unit)
    const normalized = ((deg % 360) + 360) % 360
    const rad = toRadians(normalized)
    const x = Math.cos(rad)
    const y = Math.sin(rad)

    let quadrant: string
    let refAngle: number
    if (isNearZero(normalized) || isNearZero(normalized - 90) || isNearZero(normalized - 180) || isNearZero(normalized - 270)) {
        quadrant = t({ en: 'On Axis', ru: 'На оси', lv: 'Uz Ass', pl: 'Na Osi', es: 'En el Eje', fr: 'Sur l\'Axe', it: 'Sull\'Asse', de: 'Auf der Achse' }, params.language)
        refAngle = 0
    } else if (normalized < 90) {
        quadrant = 'I'
        refAngle = normalized
    } else if (normalized < 180) {
        quadrant = 'II'
        refAngle = 180 - normalized
    } else if (normalized < 270) {
        quadrant = 'III'
        refAngle = normalized - 180
    } else {
        quadrant = 'IV'
        refAngle = 360 - normalized
    }

    return { x_coordinate: x, y_coordinate: y, reference_angle_degrees: refAngle, quadrant }
}

// ============================================================
// 12. Trigonometric Values Table Generator
// ============================================================
export function trigTableGenerator(params: { start_angle: unknown; end_angle: unknown; step_degrees: unknown; language: unknown }): { table: string } {
    const start = Number(params.start_angle) || 0
    const end = Math.max(Number(params.end_angle) || 360, start)
    const step = Math.max(Number(params.step_degrees) || 30, 1)
    const undefinedText = t(UNDEFINED_LABEL, params.language)

    const maxRows = 200
    const rows: string[] = ['Angle | sin | cos | tan']
    let count = 0
    for (let deg = start; deg <= end && count < maxRows; deg += step, count++) {
        const rad = toRadians(deg)
        const sinV = Math.sin(rad)
        const cosV = Math.cos(rad)
        const tanText = isNearZero(cosV) ? undefinedText : Math.tan(rad).toFixed(4)
        rows.push(`${deg}° | ${sinV.toFixed(4)} | ${cosV.toFixed(4)} | ${tanText}`)
    }
    return { table: rows.join('\n') }
}
